import { useMemo, useState } from 'react';
import {
  Button,
  ButtonContainer,
  Checkbox,
  FormFieldRadioButtonGroup,
  FormField,
  Heading,
  Input,
  Layout,
  RadioButton,
  Select,
} from '@vwfs-bronson/bronson-react';

import {
  EMPTY_HIDDEN_MAT_DRAFT,
  HIDDEN_FAMILY_ARRAY_FIELDS,
  HIDDEN_FIELD_DEFINITIONS,
  HIDDEN_MAT_ARRAY_FIELDS,
} from './constants.js';
import { deepClone } from './utils.js';
import { Section } from './Section.jsx';

export const HiddenFieldsEditor = ({ getCurrentValue, setValue }) => {
  const [selectedHiddenField, setSelectedHiddenField] = useState(
    HIDDEN_FIELD_DEFINITIONS[0].path,
  );
  const [hiddenMatDraft, setHiddenMatDraft] = useState(EMPTY_HIDDEN_MAT_DRAFT);
  const [hiddenBrandKey, setHiddenBrandKey] = useState('');
  const [hiddenBrandValue, setHiddenBrandValue] = useState('');
  const [hiddenFamilyKey, setHiddenFamilyKey] = useState('FI');
  const [hiddenFamilyDraft, setHiddenFamilyDraft] = useState({
    Code: '',
    Name: '',
    Description: '',
    Amount: 0,
    Type: '',
    IsFree: true,
  });
  const [hiddenNumberValue, setHiddenNumberValue] = useState(0);
  const [hiddenTextValue, setHiddenTextValue] = useState('');

  const currentHiddenValue = useMemo(() => getCurrentValue(selectedHiddenField), [
    getCurrentValue,
    selectedHiddenField,
  ]);

  const isMatHiddenField = HIDDEN_MAT_ARRAY_FIELDS.has(selectedHiddenField);
  const isFamilyArrayHiddenField = HIDDEN_FAMILY_ARRAY_FIELDS.has(selectedHiddenField);
  const isBrandEnumHiddenField = selectedHiddenField === 'Vehicle.BrandEnum';
  const isNumberHiddenField = selectedHiddenField === 'Vehicle.OptExcAmount';
  const isTextHiddenField = selectedHiddenField === 'Vehicle.prodsaDS';

  const handleDeleteHiddenField = () => {
    const selectedDefinition = HIDDEN_FIELD_DEFINITIONS.find(
      ({ path }) => path === selectedHiddenField,
    );

    if (!selectedDefinition) {
      return;
    }

    setValue(selectedHiddenField, deepClone(selectedDefinition.defaultValue));
  };

  const handleAddHiddenMatItem = () => {
    const currentArray = Array.isArray(currentHiddenValue) ? currentHiddenValue : [];
    const nextItem = {
      Code: hiddenMatDraft.Code,
      Name: hiddenMatDraft.Name,
      Description: hiddenMatDraft.Description || null,
      Amount: Number(hiddenMatDraft.Amount || 0),
      Type: hiddenMatDraft.Type || null,
      IsFree: Boolean(hiddenMatDraft.IsFree),
    };

    setValue(selectedHiddenField, [...currentArray, nextItem]);
  };

  const handleRemoveHiddenMatItem = (index) => {
    const currentArray = Array.isArray(currentHiddenValue) ? currentHiddenValue : [];
    const nextArray = currentArray.filter((_, itemIndex) => itemIndex !== index);

    if (selectedHiddenField === 'Vehicle.CampainMat' && nextArray.length === 0) {
      setValue(selectedHiddenField, null);
      return;
    }

    setValue(selectedHiddenField, nextArray);
  };

  const handleAddBrandEnumEntry = () => {
    if (!hiddenBrandKey.trim()) {
      return;
    }

    const currentObj =
      currentHiddenValue &&
      typeof currentHiddenValue === 'object' &&
      !Array.isArray(currentHiddenValue)
        ? currentHiddenValue
        : {};

    setValue(selectedHiddenField, {
      ...currentObj,
      [hiddenBrandKey]: hiddenBrandValue,
    });
  };

  const handleRemoveBrandEnumEntry = (key) => {
    const currentObj =
      currentHiddenValue &&
      typeof currentHiddenValue === 'object' &&
      !Array.isArray(currentHiddenValue)
        ? currentHiddenValue
        : {};

    setValue(
      selectedHiddenField,
      Object.fromEntries(
        Object.entries(currentObj).filter(([entryKey]) => entryKey !== key),
      ),
    );
  };

  const handleAddFamilyArrayValue = () => {
    if (!hiddenFamilyDraft.Code.trim() && !hiddenFamilyDraft.Name.trim()) {
      return;
    }

    const currentArray = Array.isArray(currentHiddenValue) && currentHiddenValue.length > 0
      ? currentHiddenValue
      : [{ FI: [] }, { RE: [] }];

    const normalized = currentArray.map((entry) => {
      if (Object.prototype.hasOwnProperty.call(entry, hiddenFamilyKey)) {
        const values = Array.isArray(entry[hiddenFamilyKey]) ? entry[hiddenFamilyKey] : [];
        const nextItem = {
          Code: hiddenFamilyDraft.Code,
          Name: hiddenFamilyDraft.Name,
          Description: hiddenFamilyDraft.Description || null,
          Amount: Number(hiddenFamilyDraft.Amount || 0),
          Type: hiddenFamilyDraft.Type || null,
          IsFree: Boolean(hiddenFamilyDraft.IsFree),
        };

        return {
          ...entry,
          [hiddenFamilyKey]: [...values, nextItem],
        };
      }

      return entry;
    });

    setValue(selectedHiddenField, normalized);
  };

  const handleRemoveFamilyArrayValue = (family, index) => {
    const currentArray = Array.isArray(currentHiddenValue) ? currentHiddenValue : [];

    const normalized = currentArray.map((entry) => {
      if (!Object.prototype.hasOwnProperty.call(entry, family)) {
        return entry;
      }

      const values = Array.isArray(entry[family]) ? entry[family] : [];
      return {
        ...entry,
        [family]: values.filter((_, valueIndex) => valueIndex !== index),
      };
    });

    const hasValues = normalized.some((entry) =>
      Object.values(entry).some((values) => Array.isArray(values) && values.length > 0),
    );

    if (!hasValues) {
      setValue(selectedHiddenField, [{ FI: [] }, { RE: [] }]);
      return;
    }

    setValue(selectedHiddenField, normalized);
  };

  return (
    <Section title="Campos ocultos (opcionales)">
      <Layout>
        <Layout.Item default="1/2" s="1/1">
          <FormField
            labelForId="hidden_field_selector"
            labelText="Campo oculto"
            type="select"
            noFloatingLabel
          >
            <Select
              id="hidden_field_selector"
              value={selectedHiddenField}
              onChange={(event) => setSelectedHiddenField(event.target.value)}
            >
              {HIDDEN_FIELD_DEFINITIONS.map(({ path }) => (
                <Select.Item key={path} value={path}>
                  {path}
                </Select.Item>
              ))}
            </Select>
          </FormField>
        </Layout.Item>

        <Layout.Item default="1/2" s="1/1">
          {isMatHiddenField && (
            <Layout>
              <Layout.Item default="1/2" s="1/1">
                <FormField labelForId="hidden_mat_code" labelText="Code" type="input" noFloatingLabel>
                  <Input
                    id="hidden_mat_code"
                    value={hiddenMatDraft.Code}
                    onChange={(event) =>
                      setHiddenMatDraft((prev) => ({ ...prev, Code: event.target.value }))
                    }
                  />
                </FormField>
              </Layout.Item>
              <Layout.Item default="1/2" s="1/1">
                <FormField labelForId="hidden_mat_name" labelText="Name" type="input" noFloatingLabel>
                  <Input
                    id="hidden_mat_name"
                    value={hiddenMatDraft.Name}
                    onChange={(event) =>
                      setHiddenMatDraft((prev) => ({ ...prev, Name: event.target.value }))
                    }
                  />
                </FormField>
              </Layout.Item>
              <Layout.Item default="1/2" s="1/1">
                <FormField
                  labelForId="hidden_mat_description"
                  labelText="Description"
                  type="input"
                  noFloatingLabel
                >
                  <Input
                    id="hidden_mat_description"
                    value={hiddenMatDraft.Description}
                    onChange={(event) =>
                      setHiddenMatDraft((prev) => ({
                        ...prev,
                        Description: event.target.value,
                      }))
                    }
                  />
                </FormField>
              </Layout.Item>
              <Layout.Item default="1/2" s="1/1">
                <FormField labelForId="hidden_mat_type" labelText="Type" type="input" noFloatingLabel>
                  <Input
                    id="hidden_mat_type"
                    value={hiddenMatDraft.Type}
                    onChange={(event) =>
                      setHiddenMatDraft((prev) => ({ ...prev, Type: event.target.value }))
                    }
                  />
                </FormField>
              </Layout.Item>
              <Layout.Item default="1/2" s="1/1">
                <FormField labelForId="hidden_mat_amount" labelText="Amount" type="input" noFloatingLabel>
                  <Input
                    id="hidden_mat_amount"
                    type="number"
                    value={hiddenMatDraft.Amount}
                    onChange={(event) =>
                      setHiddenMatDraft((prev) => ({
                        ...prev,
                        Amount: Number(event.target.value || 0),
                      }))
                    }
                  />
                </FormField>
              </Layout.Item>
              <Layout.Item default="1/2" s="1/1">
                <Checkbox
                  id="hidden_mat_is_free"
                  name="hidden_mat_is_free"
                  checked={hiddenMatDraft.IsFree}
                  onChange={(event) =>
                    setHiddenMatDraft((prev) => ({
                      ...prev,
                      IsFree: event.target.checked,
                    }))
                  }
                >
                  IsFree
                </Checkbox>
              </Layout.Item>
            </Layout>
          )}

          {isBrandEnumHiddenField && (
            <Layout>
              <Layout.Item default="1/2" s="1/1">
                <FormField labelForId="brand_enum_key" labelText="Key" type="input" noFloatingLabel>
                  <Input
                    id="brand_enum_key"
                    value={hiddenBrandKey}
                    onChange={(event) => setHiddenBrandKey(event.target.value)}
                  />
                </FormField>
              </Layout.Item>
              <Layout.Item default="1/2" s="1/1">
                <FormField labelForId="brand_enum_value" labelText="Value" type="input" noFloatingLabel>
                  <Input
                    id="brand_enum_value"
                    value={hiddenBrandValue}
                    onChange={(event) => setHiddenBrandValue(event.target.value)}
                  />
                </FormField>
              </Layout.Item>
            </Layout>
          )}

          {isFamilyArrayHiddenField && (
            <Layout>
              <Layout.Item default="1/1" s="1/1">
                <FormField labelElement="div" staticLabel type="other">
                  <FormFieldRadioButtonGroup>
                    <RadioButton
                      checked={hiddenFamilyKey === 'FI'}
                      name="hidden_family_key"
                      value="FI"
                      onChange={(event) => setHiddenFamilyKey(event.target.value)}
                    >
                      FI
                    </RadioButton>
                    <RadioButton
                      checked={hiddenFamilyKey === 'RE'}
                      name="hidden_family_key"
                      value="RE"
                      onChange={(event) => setHiddenFamilyKey(event.target.value)}
                    >
                      RE
                    </RadioButton>
                  </FormFieldRadioButtonGroup>
                </FormField>
              </Layout.Item>

              <Layout.Item default="1/2" s="1/1">
                <FormField labelForId="hidden_family_code" labelText="Code" type="input" noFloatingLabel>
                  <Input
                    id="hidden_family_code"
                    value={hiddenFamilyDraft.Code}
                    onChange={(event) =>
                      setHiddenFamilyDraft((prev) => ({ ...prev, Code: event.target.value }))
                    }
                  />
                </FormField>
              </Layout.Item>
              <Layout.Item default="1/2" s="1/1">
                <FormField labelForId="hidden_family_name" labelText="Name" type="input" noFloatingLabel>
                  <Input
                    id="hidden_family_name"
                    value={hiddenFamilyDraft.Name}
                    onChange={(event) =>
                      setHiddenFamilyDraft((prev) => ({ ...prev, Name: event.target.value }))
                    }
                  />
                </FormField>
              </Layout.Item>
              <Layout.Item default="1/2" s="1/1">
                <FormField
                  labelForId="hidden_family_description"
                  labelText="Description"
                  type="input"
                  noFloatingLabel
                >
                  <Input
                    id="hidden_family_description"
                    value={hiddenFamilyDraft.Description}
                    onChange={(event) =>
                      setHiddenFamilyDraft((prev) => ({
                        ...prev,
                        Description: event.target.value,
                      }))
                    }
                  />
                </FormField>
              </Layout.Item>
              <Layout.Item default="1/2" s="1/1">
                <FormField labelForId="hidden_family_type" labelText="Type" type="input" noFloatingLabel>
                  <Input
                    id="hidden_family_type"
                    value={hiddenFamilyDraft.Type}
                    onChange={(event) =>
                      setHiddenFamilyDraft((prev) => ({ ...prev, Type: event.target.value }))
                    }
                  />
                </FormField>
              </Layout.Item>
              <Layout.Item default="1/2" s="1/1">
                <FormField labelForId="hidden_family_amount" labelText="Amount" type="input" noFloatingLabel>
                  <Input
                    id="hidden_family_amount"
                    type="number"
                    value={hiddenFamilyDraft.Amount}
                    onChange={(event) =>
                      setHiddenFamilyDraft((prev) => ({
                        ...prev,
                        Amount: Number(event.target.value || 0),
                      }))
                    }
                  />
                </FormField>
              </Layout.Item>
              <Layout.Item default="1/2" s="1/1">
                <Checkbox
                  id="hidden_family_is_free"
                  name="hidden_family_is_free"
                  checked={hiddenFamilyDraft.IsFree}
                  onChange={(event) =>
                    setHiddenFamilyDraft((prev) => ({
                      ...prev,
                      IsFree: event.target.checked,
                    }))
                  }
                >
                  IsFree
                </Checkbox>
              </Layout.Item>
            </Layout>
          )}

          {isNumberHiddenField && (
            <FormField
              labelForId="hidden_number_value"
              labelText="Valor numérico"
              type="input"
              noFloatingLabel
            >
              <Input
                id="hidden_number_value"
                type="number"
                value={hiddenNumberValue}
                onChange={(event) => setHiddenNumberValue(Number(event.target.value || 0))}
              />
            </FormField>
          )}

          {isTextHiddenField && (
            <FormField
              labelForId="hidden_text_value"
              labelText="Valor texto"
              type="input"
              noFloatingLabel
            >
              <Input
                id="hidden_text_value"
                value={hiddenTextValue}
                onChange={(event) => setHiddenTextValue(event.target.value)}
              />
            </FormField>
          )}
        </Layout.Item>

        <Layout.Item default="1/1">
          <ButtonContainer>
            {isMatHiddenField && (
              <Button onClick={handleAddHiddenMatItem}>
                Agregar item
              </Button>
            )}

            {isBrandEnumHiddenField && (
              <Button onClick={handleAddBrandEnumEntry}>
                Agregar key/value
              </Button>
            )}

            {isFamilyArrayHiddenField && (
              <Button onClick={handleAddFamilyArrayValue}>
                Agregar item FI/RE
              </Button>
            )}

            {isNumberHiddenField && (
              <Button onClick={() => setValue(selectedHiddenField, hiddenNumberValue)}>
                Guardar número
              </Button>
            )}

            {isTextHiddenField && (
              <Button onClick={() => setValue(selectedHiddenField, hiddenTextValue)}>
                Guardar texto
              </Button>
            )}

            <Button secondary onClick={handleDeleteHiddenField}>
              Eliminar campo oculto
            </Button>
          </ButtonContainer>
        </Layout.Item>

        <Layout.Item default="1/1">
          {isMatHiddenField && Array.isArray(currentHiddenValue) && currentHiddenValue.length > 0 && (
            <Section title="Items agregados">
              {currentHiddenValue.map((item, index) => (
                <Layout key={`hidden_item_${index}`}>
                  <Layout.Item default="5/6" s="1/1">
                    <p className="u-m-none">{`${index + 1}. ${item.Code || '-'} - ${item.Name || '-'}`}</p>
                  </Layout.Item>
                  <Layout.Item default="1/6" s="1/1">
                    <Button secondary onClick={() => handleRemoveHiddenMatItem(index)}>
                      Quitar
                    </Button>
                  </Layout.Item>
                </Layout>
              ))}
            </Section>
          )}

          {isBrandEnumHiddenField &&
            currentHiddenValue &&
            typeof currentHiddenValue === 'object' &&
            !Array.isArray(currentHiddenValue) &&
            Object.keys(currentHiddenValue).length > 0 && (
              <Section title="Entries BrandEnum">
                {Object.entries(currentHiddenValue).map(([key, value]) => (
                  <Layout key={`brand_enum_${key}`}>
                    <Layout.Item default="5/6" s="1/1">
                      <p className="u-m-none">{`${key}: ${value}`}</p>
                    </Layout.Item>
                    <Layout.Item default="1/6" s="1/1">
                      <Button secondary onClick={() => handleRemoveBrandEnumEntry(key)}>
                        Quitar
                      </Button>
                    </Layout.Item>
                  </Layout>
                ))}
              </Section>
            )}

          {isFamilyArrayHiddenField && Array.isArray(currentHiddenValue) && (
            <Section title="Valores FI/RE">
              {currentHiddenValue.map((entry) =>
                Object.entries(entry).map(([family, values]) => (
                  <div key={`family_${family}`} className="u-mb-small">
                    <Heading level={5} className="u-mb-xsmall">
                      {family}
                    </Heading>
                    {(Array.isArray(values) ? values : []).map((value, index) => (
                      <Layout key={`family_${family}_${index}`}>
                        <Layout.Item default="5/6" s="1/1">
                          <p className="u-m-none">{`${value?.Code || '-'} - ${value?.Name || '-'}`}</p>
                        </Layout.Item>
                        <Layout.Item default="1/6" s="1/1">
                          <Button
                            secondary
                            onClick={() => handleRemoveFamilyArrayValue(family, index)}
                          >
                            Quitar
                          </Button>
                        </Layout.Item>
                      </Layout>
                    ))}
                  </div>
                )),
              )}
            </Section>
          )}
        </Layout.Item>
      </Layout>
    </Section>
  );
};